/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState, useRef } from 'react';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material components
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Images
import team2 from "assets/images/team-2.jpg";
import team3 from "assets/images/team-3.jpg";
import team4 from "assets/images/team-4.jpg";

import LeasesService from "services/leases-service";
import PaymentsService from "services/payments-service";

export default function PaymentsData() {
  const [paymentsData, setPaymentsData] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [innerLoading, setInnerLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isShowDialog, setIsShowDialog] = useState(false);
  const [editedRow, setEditedRow] = useState(null);
  const [sortField, setSortField] = useState("updated_at"); // default column
  const [sortDirection, setSortDirection] = useState("desc"); // or "desc"
  const totalRows = useRef(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // Add state for selected item
  const [pageIndex, setPageIndex] = useState('');
  const [propertyId, setPropertyId] = useState('all');
  const [filter, setFilter] = useState('');

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget); // Set anchorEl to the clicked element
    setSelectedItem(item); // Store the selected item
  };

  const handleMenuClose = () => {
    setAnchorEl(null); // Reset anchorEl to null
    setSelectedItem(null); // Reset selected item
  };

  const handlePendingPayment = async (item) => {
    if (window.confirm("Are you sure you want to mark this payment as Pending?")) {
      try {
        await PaymentsService.markPending({ id: item.id });
        await getPaymentsList(); // Refresh the payments list
      } catch (error) {
        console.error("Error marking payment as Pending:", error);
      }
    }
  };

  const handleApprovePayment = async (item) => {
    if (window.confirm("Are you sure you want to approve this payment?")) {
      try {
        await PaymentsService.markApprove({ id: item.id });
        await getPaymentsList(); // Refresh the payments list
      } catch (error) {
        console.error("Error approving payment:", error);
      }
    }
  };

  const handleCancelPayment = async (item) => {
    const cancelNote = window.prompt("Are you sure you want to cancel this payment? Please provide a cancel note:");
    if (cancelNote !== null) {
      try {
        await PaymentsService.markCancel({ id: item.id, cancel_notes: cancelNote });
        await getPaymentsList(); // Refresh the payments list
      } catch (error) {
        console.error("Error canceling payment:", error);
      }
    }
  };

  const getPaymentsList = async (
    pageIndexParam = pageIndex, 
    pageSize = 25, 
    filterParam = filter, 
    propertyIdParam = propertyId, 
    sortField = "updated_at", 
    sortDirection = "desc"
  ) => {
    try {
      setInnerLoading(true);
      setButtonDisabled(true);
      const data = await PaymentsService.getList({
        limit: pageSize,
        page: pageIndexParam,
        sortField: sortField,
        sortDirection: sortDirection,
        filter: filterParam,
        propertyId: propertyIdParam,
        whereField: propertyIdParam === "all" ? "" : 'property_id',
        whereValue: propertyIdParam === "all" ? "" : propertyIdParam
      });
      setTotalPages(data.meta.last_page);
      totalRows.current = data.meta.total;
      console.log(data.data);
      const paymentsList = data.data.map(({ id, amount,  payment_method: { display_name }, payment_date, tenant: { business_name, email, first_name }, lease: {lease_code}, property: { property_name }, unit_names, agreement_doc, rent_amount, due_date, tenants, total, receipt_number, status: { status_class, status_icon, status_text }, is_approved, is_pending, is_cancelled }) => ({
        id, 
        amount,
        payment_method_id: display_name,
        tenant_id: first_name || business_name || email || "Unknown",
        lease_id: lease_code,
        payment_date,
        property_id: property_name,
        unit_names, 
        agreement_doc: agreement_doc || "", 
        rent_amount, 
        tenants,
        total,
        receipt_number,
        status_class,
        status_icon,
        status_text,
        is_approved, 
        is_pending, 
        is_cancelled
      }));
      console.log(paymentsList);
      setPaymentsData(paymentsList);
    } catch (error) {
      console.error('Error get payments list:', error);
    } finally {
      setButtonDisabled(false);
      setInnerLoading(false);
    }
  };

  const handleEditPayment = (item) => {
    setOpen(true);
    setIsShowDialog(true);
    setEditedRow(item);
  };

  useEffect(() => {
    const data = paymentsData.map((item, index) => ({
      ...item,
      no: index,
      payment_status: (
        <MDBox ml={-1} className={`${item.status_class}`} display="flex" alignItems="center">
          <Icon fontSize="14px">{item.status_icon}</Icon>
          <MDTypography component="span" fontSize="14px" sx={{ marginLeft: "5px", fontWeight: "400" }} className={`${item.status_class}`}>
            {item.status_text}
          </MDTypography>
        </MDBox>
      ),
      action: (
        <MDBox display="flex" alignItems="center">
          <IconButton onClick={(event) => handleMenuOpen(event, item)}>
            <Icon>more_vert</Icon>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)} 
            onClose={handleMenuClose}
          >
            {selectedItem && (() => {
              if (selectedItem.is_cancelled) {
                return (
                  <>
                    <MenuItem onClick={() => { handleApprovePayment(selectedItem); handleMenuClose(); }}>
                      <Icon className={`${'text-success'}`}>check</Icon>&nbsp; Approve
                    </MenuItem>
                    <MenuItem onClick={() => { handlePendingPayment(selectedItem); handleMenuClose(); }}>
                      <Icon className={`${'text-info'}`}>access_time</Icon>&nbsp; Pending
                    </MenuItem>
                  </>
                );
              }
              if (selectedItem.is_approved) {
                return (
                  <>
                    <MenuItem onClick={() => { window.location.href = `/payments/${selectedItem.id}`; handleMenuClose(); }}>
                      <Icon className={`${'text-primary'}`}>receipt</Icon>&nbsp; Receipt
                    </MenuItem>
                    <MenuItem onClick={() => { handlePendingPayment(selectedItem); handleMenuClose(); }}>
                      <Icon className={`${'text-info'}`}>access_time</Icon>&nbsp; Pending
                    </MenuItem>
                    <MenuItem onClick={() => { handleCancelPayment(selectedItem); handleMenuClose(); }}>
                      <Icon className={`${'text-danger'}`}>close</Icon>&nbsp; Cancel
                    </MenuItem>
                  </>
                );
              }
              if (selectedItem.is_pending) {
                return (
                  <>
                    <MenuItem onClick={() => { handleApprovePayment(selectedItem); handleMenuClose(); }}>
                      <Icon className={`${'text-success'}`}>check</Icon>&nbsp; Approve
                    </MenuItem>
                    <MenuItem onClick={() => { handleCancelPayment(selectedItem); handleMenuClose(); }}>
                      <Icon className={`${'text-danger'}`}>close</Icon>&nbsp; Cancel
                    </MenuItem>
                  </>
                );
              }
              return null;
            })()}
          </Menu>
        </MDBox>
      )
    }));
    setRows(data);
  }, [paymentsData, anchorEl]);

  return {
    columns: [
      { Header: "amount", accessor: "amount", align: "left" },
      { Header: "payment method", accessor: "payment_method_id", align: "left", contentWidth: "125px" },
      { Header: "payment date", accessor: "payment_date", align: "left", contentWidth: "125px"},
      { Header: "tenant", accessor: "tenant_id", align: "left" },
      { Header: "tenant code", accessor: "lease_id", align: "left" },
      { Header: "property", accessor: "property_id", align: "left" },
      { Header: "receipt number", accessor: "receipt_number", align: "left" },
      { Header: "status", accessor: "payment_status", align: "left" },
      { Header: "action", accessor: "action", enableSorting: false, align: "left" }
    ],
    rows: rows,
    totalPages: totalPages,
    getPaymentsList: getPaymentsList,
    innerLoading: innerLoading,
    buttonDisabled: buttonDisabled,
    setButtonDisabled: setButtonDisabled,
    open: open,
    setOpen: setOpen,
    isShowDialog: isShowDialog,
    sortField: sortField,
    setSortField: setSortField,
    sortDirection: sortDirection,
    setSortDirection: setSortDirection,
    editedRow: editedRow,
    totalRows: totalRows,
    pageIndex,
    setPageIndex,
    propertyId,
    setPropertyId,
    filter,
    setFilter
  };
}
